import type { PlcBinding } from '@/io/types';
import type { Device } from '@/devices/types';
import type { HMIProject } from '@/hmi/model/types';

/** Version of the `.plcsim` document layout. Migrations arrive in Phase 16 (TD-013). */
export const PROJECT_FORMAT_VERSION = 1;

export interface ProjectMetadata {
  readonly name: string;
  readonly description: string;
  /** ISO-8601 timestamps. Injected by callers so project creation stays deterministic. */
  readonly createdAt: string;
  readonly modifiedAt: string;
  readonly appVersion: string;
}

export type PipePortType = 'inlet' | 'outlet';

export interface PipePortRef {
  readonly deviceId: string;
  readonly portType: PipePortType;
  readonly portId?: string | undefined;
}

export interface PipeConnection {
  readonly id: string;
  readonly from: PipePortRef;
  readonly to: PipePortRef;
  readonly waypoints?: readonly { readonly x: number; readonly y: number }[] | undefined;
  readonly color?: string | undefined;
  readonly diameter?: number | undefined;
}

export interface ProjectScene {
  readonly devices: readonly Device[];
  readonly pipes?: readonly PipeConnection[] | undefined;
}

import type { LadderProgram } from '../ladder/model/LadderProgram';
import type { WorkshopProject } from '@/workshop/model/types';

export interface ProjectPlc {
  readonly scanTimeMs: number;
  readonly addressing: 'generic';
  readonly bindings: readonly PlcBinding[];
  readonly program: LadderProgram;
}

/**
 * Project document skeleton (ARCHITECTURE §5.8). It only describes the future shape;
 * serialization, schema validation and migrations are Phase 16 work.
 */
export interface Project {
  readonly formatVersion: typeof PROJECT_FORMAT_VERSION;
  readonly metadata: ProjectMetadata;
  readonly scene: ProjectScene;
  readonly plc: ProjectPlc;
  readonly hmi: HMIProject;
  readonly workshop?: WorkshopProject | undefined;
  /** Lessons and tasks arrive in Phase 17. */
  readonly education: null;
}

import { createEmptyHMIProject } from '@/hmi/model/types';

export const DEFAULT_SCAN_TIME_MS = 10;

export function createEmptyProject(input: {
  readonly name: string;
  readonly nowIso: string;
  readonly appVersion: string;
}): Project {
  return {
    formatVersion: PROJECT_FORMAT_VERSION,
    metadata: {
      name: input.name,
      description: '',
      createdAt: input.nowIso,
      modifiedAt: input.nowIso,
      appVersion: input.appVersion,
    },
    scene: { devices: [], pipes: [] },
    plc: { 
      scanTimeMs: DEFAULT_SCAN_TIME_MS, 
      addressing: 'generic', 
      bindings: [], 
      program: { 
        networks: [
          {
            id: `net_${Date.now()}`,
            name: 'Network 1',
            elements: {}
          }
        ] 
      } 
    },
    hmi: createEmptyHMIProject(),
    education: null,
  };
}

export function normalizeProject(raw: any, fallbackName = 'Proje', appVersion = '0.1.0'): Project {
  const empty = createEmptyProject({
    name: typeof raw?.metadata?.name === 'string' && raw.metadata.name.trim() ? raw.metadata.name : fallbackName,
    nowIso: new Date().toISOString(),
    appVersion,
  });

  if (!raw || typeof raw !== 'object') {
    return empty;
  }

  // 1. Detect if the input is directly an array of networks or ladder program
  let candidateNetworks: any[] | null = null;
  if (Array.isArray(raw)) {
    candidateNetworks = raw;
  } else if (Array.isArray(raw.networks)) {
    candidateNetworks = raw.networks;
  } else if (raw.program && Array.isArray(raw.program.networks)) {
    candidateNetworks = raw.program.networks;
  } else if (raw.plc?.program && Array.isArray(raw.plc.program.networks)) {
    candidateNetworks = raw.plc.program.networks;
  }

  // 2. Metadata
  const metadata = {
    name: typeof raw.metadata?.name === 'string' && raw.metadata.name.trim() ? raw.metadata.name : empty.metadata.name,
    description: typeof raw.metadata?.description === 'string' ? raw.metadata.description : '',
    createdAt: typeof raw.metadata?.createdAt === 'string' ? raw.metadata.createdAt : empty.metadata.createdAt,
    modifiedAt: typeof raw.metadata?.modifiedAt === 'string' ? raw.metadata.modifiedAt : empty.metadata.modifiedAt,
    appVersion: typeof raw.metadata?.appVersion === 'string' ? raw.metadata.appVersion : appVersion,
  };

  // 3. Scene (devices and pipes)
  let rawDevices = raw.scene?.devices || (Array.isArray(raw.devices) ? raw.devices : []);
  if (!Array.isArray(rawDevices)) rawDevices = [];

  const normalizedDevices = rawDevices.map((d: any, idx: number) => {
    const id = d?.id || `dev_${idx}_${Date.now()}`;
    const type = d?.type || 'motor';
    const name = d?.name || `Device ${idx + 1}`;
    const props = (d?.props && typeof d.props === 'object') ? { ...d.props } : {};

    let x = 0;
    let y = 0;
    let rot = 0;
    if (d?.transform?.position) {
      x = Number(d.transform.position.x) || 0;
      y = Number(d.transform.position.y) || 0;
      rot = Number(d.transform.rotation) || 0;
    } else if (d?.transform) {
      x = Number(d.transform.x ?? d.x) || 0;
      y = Number(d.transform.y ?? d.y) || 0;
      rot = Number(d.transform.rotation ?? d.rotation) || 0;
    } else {
      x = Number(d?.x) || 0;
      y = Number(d?.y) || 0;
      rot = Number(d?.rotation) || 0;
    }

    return {
      id,
      type,
      name,
      props,
      transform: {
        position: { x, y },
        rotation: (rot % 4) as any,
      },
    };
  });

  let rawPipes = raw.scene?.pipes || (Array.isArray(raw.pipes) ? raw.pipes : []);
  if (!Array.isArray(rawPipes)) rawPipes = [];

  const normalizedPipes = rawPipes.map((p: any, idx: number) => {
    return {
      id: p?.id || `pipe_${idx}_${Date.now()}`,
      from: {
        deviceId: p?.from?.deviceId || '',
        portType: p?.from?.portType || 'outlet',
        portId: p?.from?.portId,
      },
      to: {
        deviceId: p?.to?.deviceId || '',
        portType: p?.to?.portType || 'inlet',
        portId: p?.to?.portId,
      },
      waypoints: Array.isArray(p?.waypoints)
        ? p.waypoints
            .filter((w: any) => typeof w?.x === 'number' && typeof w?.y === 'number')
            .map((w: any) => ({ x: Number(w.x), y: Number(w.y) }))
        : undefined,
      color: p?.color,
      diameter: p?.diameter,
    };
  });

  // 4. PLC & Program
  let networksToProcess: any[] = [];
  if (candidateNetworks) {
    networksToProcess = candidateNetworks;
  } else if (raw.plc?.program?.networks && Array.isArray(raw.plc.program.networks)) {
    networksToProcess = raw.plc.program.networks;
  } else {
    networksToProcess = empty.plc.program.networks;
  }

  const normalizedNetworks = networksToProcess.map((net: any, netIdx: number) => {
    const netId = net?.id || `net_${netIdx + 1}`;
    const netName = net?.name || `Network ${netIdx + 1}`;
    const comment = typeof net?.comment === 'string' ? net.comment : '';

    let elementsObj: Record<string, any> = {};
    if (net?.elements && typeof net.elements === 'object') {
      const rawEls = Array.isArray(net.elements) ? net.elements : Object.values(net.elements);
      for (const el of rawEls) {
        if (!el || !el.id) continue;
        const normEl = { ...el };
        const t = normEl.type;
        if (['CMP_EQ_I', 'CMP_NE_I', 'CMP_GT_I', 'CMP_LT_I', 'CMP_GE_I', 'CMP_LE_I'].includes(t)) {
          if (typeof normEl.in1 !== 'string') normEl.in1 = 'MW0';
          if (typeof normEl.in2 !== 'string') normEl.in2 = '0';
        } else if (['ADD_I', 'SUB_I', 'MUL_I', 'DIV_I'].includes(t)) {
          if (typeof normEl.in1 !== 'string') normEl.in1 = 'MW0';
          if (typeof normEl.in2 !== 'string') normEl.in2 = '1';
          if (typeof normEl.out !== 'string') normEl.out = 'MW2';
        } else if (t === 'MOV_W' || t === 'MOV_R') {
          if (typeof normEl.in !== 'string') normEl.in = (t === 'MOV_R' ? 'MD0' : 'MW0');
          if (typeof normEl.out !== 'string') normEl.out = (t === 'MOV_R' ? 'MD4' : 'MW2');
        } else if (['TON', 'TOF', 'TP'].includes(t)) {
          if (typeof normEl.address !== 'string') normEl.address = 'T1';
          if (typeof normEl.presetTime !== 'string') normEl.presetTime = '5s';
        } else if (['CTU', 'CTD', 'CTUD'].includes(t)) {
          if (typeof normEl.address !== 'string') normEl.address = 'C1';
          if (typeof normEl.presetValue !== 'string') normEl.presetValue = '5';
        } else if (['NO_CONTACT', 'NC_CONTACT', 'COIL', 'SET_COIL', 'RESET_COIL'].includes(t)) {
          if (typeof normEl.address !== 'string') normEl.address = t.includes('COIL') ? 'Q0.0' : 'I0.0';
        }
        if (['COIL', 'SET_COIL', 'RESET_COIL', 'INVERT_COIL', 'JMP', 'LBL'].includes(t)) {
          normEl.column = 11;
        }
        elementsObj[normEl.id] = normEl;
      }
    }

    return {
      id: netId,
      name: netName,
      comment,
      elements: elementsObj,
    };
  });

  if (normalizedNetworks.length === 0) {
    normalizedNetworks.push({
      id: `net_${Date.now()}`,
      name: 'Network 1',
      comment: '',
      elements: {},
    });
  }

  const plc = {
    scanTimeMs: Number(raw.plc?.scanTimeMs) || DEFAULT_SCAN_TIME_MS,
    addressing: 'generic' as const,
    bindings: Array.isArray(raw.plc?.bindings) ? raw.plc.bindings : [],
    program: {
      networks: normalizedNetworks,
    },
  };

  // 5. HMI
  let hmi = raw.hmi;
  if (!hmi || !Array.isArray(hmi.screens) || hmi.screens.length === 0) {
    hmi = createEmptyHMIProject();
  } else {
    hmi = {
      screens: hmi.screens.map((sc: any, scIdx: number) => ({
        id: sc?.id || `screen_${scIdx + 1}`,
        name: sc?.name || `Screen ${scIdx + 1}`,
        components: Array.isArray(sc?.components) ? sc.components : [],
        backgroundColor: sc?.backgroundColor || '#1e1e1e',
      })),
      defaultScreenId: hmi.defaultScreenId || hmi.screens[0]?.id || 'screen_1',
    };
  }

  // 6. Workshop (Elektrik Kumanda Atölyesi)
  let workshop: WorkshopProject | undefined = undefined;
  if (raw.workshop && Array.isArray(raw.workshop.devices) && Array.isArray(raw.workshop.wires)) {
    workshop = {
      id: typeof raw.workshop.id === 'string' ? raw.workshop.id : 'circuit_dol_starter',
      name: typeof raw.workshop.name === 'string' ? raw.workshop.name : 'Mühürlemeli Doğrudan Motor Kumanda Devresi',
      devices: raw.workshop.devices,
      wires: raw.workshop.wires,
    };
  }

  return {
    formatVersion: PROJECT_FORMAT_VERSION,
    metadata,
    scene: {
      devices: normalizedDevices,
      pipes: normalizedPipes,
    },
    plc,
    hmi,
    workshop,
    education: null,
  };
}
